import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useCasesData } from '@/data/useCasesData';
import { Link } from 'react-router-dom';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const containerFadeIn = {
  initial: { opacity: 0 },
  whileInView: { 
    opacity: 1,
    transition: {
      duration: 0.5 
    }
  },
  viewport: { once: true, amount: 0.2 },
};

const UseCasesSection = () => {
  return (
    <section id="solutions" className="py-20 bg-background border-t border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.5 }}
          variants={fadeInUp}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 font-heading text-primary-foreground">
            Powerful <span className="gradient-text-brand">Solutions</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Discover how Webfiny empowers businesses across various industries to optimize their document workflows.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerFadeIn}
          initial="initial"
          whileInView="whileInView"
        >
          {useCasesData.map((solution) => (
            <div
              key={solution.slug}
              className="bg-card p-8 rounded-xl shadow-xl hover-lift border border-border flex flex-col justify-between"
            >
              <div>
                <solution.icon className="h-10 w-10 text-primary mb-6" />
                <h3 className="text-2xl font-bold mb-3 text-card-foreground font-heading">{solution.title}</h3>
                <p className="text-foreground text-sm mb-6 leading-relaxed">{solution.description}</p>
              </div>
              <Link to={`/solutions/${solution.slug}`} aria-label={`Learn more about ${solution.title}`}>
                <Button 
                  variant="link" 
                  className="text-primary p-0 hover:text-primary/80 group font-semibold"
                >
                  Learn More <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          ))}
        </motion.div>
        
        <motion.div
          className="text-center mt-16"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.5 }}
          variants={fadeInUp}
        >
          <Link to="/solutions" aria-label="View All WebFiny Solutions">
            <Button 
              size="lg" 
              variant="outline" 
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
            >
              View All Solutions
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default UseCasesSection;